import { Helmet } from "react-helmet-async"
import { Event, AgeRangeEnum } from "@/common/types"
import useGetEvent from "@/hooks/api/events/useGetEvent"
import Logo from "@/common/images/843logo_white.png"

//Gets a single numeric price for schema.org data
// $20.00 -> 20.00, FREE -> 0, $5-$10 -> 5 (uses the low end of the range)
function parseSchemaPrice(rawPrice: string): string {
  if (rawPrice.toUpperCase().includes("FREE")) {
    return "0"
  }
  const matches = rawPrice.match(/\d+(\.\d+)?/g)
  if (!matches || matches.length === 0) {
    return "0"
  }
  return matches[0]
}

// Same URL shape BackEnd/src/utils/sitemap.ts generates for this event - keep the two
// in sync if either changes. Always the calendar-style URL, regardless of which page
// (calendar/list/map) the popup happens to be open on, so the schema's url always
// matches the one canonical URL the sitemap actually lists for this event.
function buildCanonicalEventUrl(eventId: number, startTimestamp: Date): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "numeric",
    day: "numeric",
  }).formatToParts(startTimestamp)
  const year = Number(parts.find((p) => p.type === "year")!.value)
  const month = Number(parts.find((p) => p.type === "month")!.value)
  const day = Number(parts.find((p) => p.type === "day")!.value)
  const dayTimestamp = Date.UTC(year, month - 1, day, 12, 0, 0)

  return `/calendar/${year}/${month}?eventId=${eventId}&details=true&date=${dayTimestamp}`
}

//Maps age_range enum to schema.org, ANY maps to Family
function mapAudienceType(ageRange: AgeRangeEnum): string {
  if (ageRange === AgeRangeEnum.ADULT) return "Adult"
  if (ageRange === AgeRangeEnum.TEEN) return "Teen"
  return "Family"
}

// Injects the per-event <title>, meta description, and JSON-LD schema for whichever
// event is currently open. Fetches directly by id so it doesn't depend on the event
// being in whatever list/day/map bounds happen to already be loaded. Shared by the
// calendar, list, and map pages so all three carry the same SEO payload.
const EventSeoMeta: React.FC<{ eventId: number }> = ({ eventId }) => {
  const { data: eventResponse } = useGetEvent(eventId)
  const eventData = eventResponse?.data as Event | undefined

  if (!eventData) return null

  const EventSchema = {
    "@context": "https://schema.org",
    "@type": "Event",
    name: eventData.title,
    description: eventData.description,
    startDate: eventData.event_start_timestamp,
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    location: {
      "@type": "Place",
      name: eventData.location_title,
      address: eventData.address,
    },
    audience: {
      "@type": "Audience",
      audienceType: mapAudienceType(eventData.age_range),
    },
    image: eventData.event_images?.length
      ? [eventData.event_images[0].image_url]
      : [`${window.location.origin}${Logo}`],
    offers: {
      "@type": "Offer",
      price: parseSchemaPrice(eventData.price),
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
      url: eventData.action_url,
    },
    // fixed per event - same regardless of which page the popup is open on
    url: `${window.location.origin}${buildCanonicalEventUrl(eventData.event_id, eventData.event_start_timestamp)}`,
  }

  return (
    <Helmet>
      <title>{`${eventData.title} | 843Hub`}</title>
      <meta name="description" content={eventData.description} />
      <script type="application/ld+json">{JSON.stringify(EventSchema)}</script>
    </Helmet>
  )
}

export default EventSeoMeta
